import React, { useEffect, useRef, useState } from 'react';
import * as atlas from 'azure-maps-control';
import 'azure-maps-control/dist/atlas.min.css';
import { Spin, Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { getMapsToken } from '../services/MapsService';
import { IGeofenceZone, IGeofencePoint } from '../interfaces/heavyDutyMachinery/IGeofence';
import { circleToRing, zoomForRadius } from '../helpers/geoCircle';
import { TEAL, TEAL_LIGHT, CORAL } from '../theme/creamyTokens';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';

const { Text } = Typography;

const ZONE_SOURCE_ID = 'geofence-zone-source';
const REFERENCE_SOURCE_ID = 'geofence-reference-source';
const VERTEX_SOURCE_ID = 'geofence-vertex-source';
const MACHINE_SOURCE_ID = 'geofence-machine-source';
const MACHINE_ICON_ID = 'geofence-machine-icon';

// Same treatment SpacesMap gives its own canvas -- the map is a backdrop for
// the zone, not the subject, and full-saturation road colours fight the
// teal fill for attention.
const MAP_CANVAS_FILTER = 'saturate(0.55) brightness(1.10)';

export interface IGeofenceMapProps {
  /** The zone to draw. A suggestion draws exactly like a saved one: the difference is what the card around this says about it, not what it looks like. */
  zone?: IGeofenceZone;

  /** Where the machine is right now, when it is reporting at all. */
  machineLatitude?: number;
  machineLongitude?: number;

  /** True while the machine is outside -- turns the fill from teal to coral. */
  isBreached?: boolean;

  /**
   * A second shape worth seeing next to the first, drawn as a dashed outline
   * and never filled: the zone as it stands while a move is being proposed,
   * or the proposal while the zone stands. Whoever is deciding should be able
   * to SEE what they are deciding, not just read a sentence about it.
   */
  referenceZone?: IGeofenceZone;

  /** When set, tapping the map moves the centre. Only ever passed by the host's own editor, on a circle zone. */
  onCenterPicked?: (latitude: number, longitude: number) => void;

  /**
   * When set, the map is a polygon editor: tapping adds a vertex at the end,
   * dragging one moves it, and this is called with the whole new list every
   * time. Vertices are only drawn while this is set -- a saved polygon shows
   * its outline and nothing else.
   */
  onPointsChanged?: (points: IGeofencePoint[]) => void;

  height?: number;
}

/**
 * One booking's work zone on a live map: the fence, and the machine inside
 * or outside it.
 *
 * Azure Maps has no geodesic circle in metres, so the circle is drawn as a
 * 64-sided polygon computed on the sphere (see circleToRing) -- the flat
 * version would draw a visible egg at Spanish latitudes, and an egg is a
 * fence somebody can be accused of crossing.
 *
 * Deliberately not azure-maps-drawing-tools: that package's toolbar is built
 * for a mouse, and what this needs is one tap to put the centre where the
 * site is. Placing a point on our own layers is less code and the right
 * gesture on a phone at a jobsite.
 */
const GeofenceMap: React.FC<IGeofenceMapProps> = ({
  zone,
  machineLatitude,
  machineLongitude,
  isBreached,
  referenceZone,
  onCenterPicked,
  onPointsChanged,
  height = 260,
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<atlas.Map | undefined>(undefined);
  const zoneSourceRef = useRef<atlas.source.DataSource | undefined>(undefined);
  const referenceSourceRef = useRef<atlas.source.DataSource | undefined>(undefined);
  const machineSourceRef = useRef<atlas.source.DataSource | undefined>(undefined);
  const vertexSourceRef = useRef<atlas.source.DataSource | undefined>(undefined);
  /** Index of the vertex currently under the finger, or null. Lives in a ref because the drag handlers are registered once and would otherwise close over a stale value. */
  const draggingVertexRef = useRef<number | null>(null);
  const [ready, setReady] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  // The tap handler is registered once, on map ready, but has to keep seeing
  // the CURRENT callback -- re-registering it on every render would stack
  // listeners, and capturing the first one would freeze the editor on its
  // initial state.
  const onCenterPickedRef = useRef(onCenterPicked);
  onCenterPickedRef.current = onCenterPicked;
  const onPointsChangedRef = useRef(onPointsChanged);
  onPointsChangedRef.current = onPointsChanged;
  // The handlers need the CURRENT vertex list to append to or edit, and they
  // are registered once on map ready -- so the list goes through a ref too.
  const pointsRef = useRef<IGeofencePoint[]>([]);
  pointsRef.current = zone?.kind === 'Polygon' ? zone.points : [];
  // The camera effect below needs to re-run when a polygon's corners change,
  // and a fresh array arrives on every render -- so it depends on the corners
  // themselves rather than on the array holding them.
  const polygonBoundsKey =
    zone?.kind === 'Polygon' ? zone.points.map((point) => `${point.latitude},${point.longitude}`).join('|') : '';

  useEffect(() => {
    let cancelled = false;
    let map: atlas.Map | undefined;

    const init = async () => {
      const mapsToken = await getMapsToken().catch(() => undefined);
      if (cancelled) return;
      if (!mapsToken || !containerRef.current) {
        setUnavailable(true);
        return;
      }

      map = new atlas.Map(containerRef.current, {
        center: initialCenter(zone),
        zoom: initialZoom(zone),
        language: 'es-ES',
        style: 'road',
        showLogo: false,
        showFeedbackLink: false,
        authOptions: {
          authType: atlas.AuthenticationType.anonymous,
          clientId: mapsToken.clientId,
          getToken: (resolve, reject) => {
            getMapsToken()
              .then((refreshed) => (refreshed ? resolve(refreshed.token) : reject(new Error('No map token'))))
              .catch(reject);
          },
        },
      });
      mapRef.current = map;

      map.events.add('ready', async () => {
        if (cancelled || !map) return;

        const canvas = containerRef.current?.querySelector('canvas');
        if (canvas) (canvas as HTMLCanvasElement).style.filter = MAP_CANVAS_FILTER;

        const zoneSource = new atlas.source.DataSource(ZONE_SOURCE_ID);
        const referenceSource = new atlas.source.DataSource(REFERENCE_SOURCE_ID);
        const machineSource = new atlas.source.DataSource(MACHINE_SOURCE_ID);
        const vertexSource = new atlas.source.DataSource(VERTEX_SOURCE_ID);
        map.sources.add([zoneSource, referenceSource, machineSource, vertexSource]);
        zoneSourceRef.current = zoneSource;
        referenceSourceRef.current = referenceSource;
        machineSourceRef.current = machineSource;
        vertexSourceRef.current = vertexSource;

        // Fill first, then its own outline on top: a translucent fill alone
        // reads as a smudge at the zoom a whole site is seen at.
        map.layers.add(
          new atlas.layer.PolygonLayer(zoneSource, undefined, {
            fillColor: ['get', 'color'],
            fillOpacity: 0.18,
          }),
        );
        map.layers.add(
          new atlas.layer.LineLayer(zoneSource, undefined, {
            strokeColor: ['get', 'color'],
            strokeWidth: 2,
          }),
        );

        // A proposal is an outline and nothing else: dashed, unfilled, and in
        // the lighter teal. Filling it would make two zones compete for the
        // same ground, and the one that exists has to win that.
        map.layers.add(
          new atlas.layer.LineLayer(referenceSource, undefined, {
            strokeColor: TEAL_LIGHT,
            strokeWidth: 2,
            strokeDashArray: [3, 2],
          }),
        );

        await map.imageSprite.createFromTemplate(MACHINE_ICON_ID, 'marker-thick', CORAL, '#FFFFFF');
        map.layers.add(
          new atlas.layer.SymbolLayer(machineSource, undefined, {
            iconOptions: { image: MACHINE_ICON_ID, allowOverlap: true, anchor: 'bottom', size: 0.9 },
          }),
        );

        // The draggable vertices. A plain circle rather than a pin: a pin
        // points at a spot from above it, and a vertex IS the spot -- with a
        // pin the corner of the fence sits wherever the tip happens to land.
        // Big enough (14px) to be a finger target, which a 6px dot is not.
        const vertexLayer = new atlas.layer.BubbleLayer(vertexSource, undefined, {
          radius: 9,
          color: TEAL,
          strokeColor: '#FFFFFF',
          strokeWidth: 3,
        });
        const vertexLabels = new atlas.layer.SymbolLayer(vertexSource, undefined, {
          iconOptions: { image: 'none' },
          textOptions: {
            textField: ['to-string', ['+', ['get', 'index'], 1]],
            offset: [0, -1.6],
            size: 12,
            color: TEAL,
            haloColor: '#FFFFFF',
            haloWidth: 2,
          },
        });
        map.layers.add([vertexLayer, vertexLabels]);

        // ---- Dragging a vertex ------------------------------------------
        // Azure Maps has no draggable-feature primitive, so this is the
        // standard three-step: grab on the layer, follow on the map, let go
        // anywhere. Panning is switched off for the duration, or the map
        // slides away under the finger instead of the vertex moving.
        const startDrag = (event: atlas.MapMouseEvent | atlas.MapTouchEvent) => {
          if (!onPointsChangedRef.current) return;
          const index = (event.shapes?.[0] as atlas.Shape | undefined)?.getProperties()?.index;
          if (typeof index !== 'number') return;
          draggingVertexRef.current = index;
          map?.setUserInteraction({ dragPanInteraction: false });
        };

        const moveDrag = (event: atlas.MapMouseEvent | atlas.MapTouchEvent) => {
          const index = draggingVertexRef.current;
          const pick = onPointsChangedRef.current;
          if (index === null || !pick || !event.position) return;
          const next = pointsRef.current.map((point, i) =>
            i === index ? { latitude: event.position![1], longitude: event.position![0] } : point,
          );
          pick(next);
        };

        const endDrag = () => {
          if (draggingVertexRef.current === null) return;
          draggingVertexRef.current = null;
          map?.setUserInteraction({ dragPanInteraction: true });
        };

        map.events.add('mousedown', vertexLayer, startDrag);
        map.events.add('touchstart', vertexLayer, startDrag);
        map.events.add('mousemove', moveDrag);
        map.events.add('touchmove', moveDrag);
        map.events.add('mouseup', endDrag);
        map.events.add('touchend', endDrag);

        map.events.add('click', (event: atlas.MapMouseEvent) => {
          if (!event.position) return;
          // atlas positions are [longitude, latitude]; everything else in
          // this app says latitude first. Swapped exactly here, once.
          const latitude = event.position[1];
          const longitude = event.position[0];

          // Letting go of a vertex fires a click too. Adding a new vertex on
          // top of the one just dragged is the one thing that would make the
          // editor feel broken, so the drag's own click is swallowed.
          if (draggingVertexRef.current !== null) return;

          const addPoint = onPointsChangedRef.current;
          if (addPoint) {
            addPoint([...pointsRef.current, { latitude, longitude }]);
            return;
          }

          onCenterPickedRef.current?.(latitude, longitude);
        });

        setReady(true);
      });
    };

    init();

    return () => {
      cancelled = true;
      map?.dispose();
      mapRef.current = undefined;
      zoneSourceRef.current = undefined;
      referenceSourceRef.current = undefined;
      machineSourceRef.current = undefined;
      vertexSourceRef.current = undefined;
    };
    // Built once. Everything that changes afterwards is pushed into the data
    // sources by the effects below rather than by rebuilding the map, which
    // would flash and lose the viewer's own pan and zoom.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- The zone itself -------------------------------------------------
  useEffect(() => {
    const source = zoneSourceRef.current;
    if (!ready || !source) return;

    source.clear();
    if (!zone) return;

    const color = isBreached ? CORAL : TEAL;

    // A polygon halfway through being drawn is not a shape yet. Joining the
    // two points it does have keeps the map showing progress instead of
    // going blank until the third tap.
    if (zone.kind === 'Polygon' && zone.points.length === 2) {
      source.add(
        new atlas.data.Feature(
          new atlas.data.LineString(zone.points.map((p) => [p.longitude, p.latitude] as [number, number])),
          { color },
        ),
      );
      return;
    }

    const ring = toRing(zone);
    if (ring.length === 0) return;

    source.add(new atlas.data.Feature(new atlas.data.Polygon([ring]), { color }));
  }, [ready, zone, isBreached]);

  // ---- The other shape, the dashed one ---------------------------------
  useEffect(() => {
    const source = referenceSourceRef.current;
    if (!ready || !source) return;

    source.clear();
    if (!referenceZone) return;

    const ring = toRing(referenceZone);
    if (ring.length === 0) return;

    source.add(new atlas.data.Feature(new atlas.data.Polygon([ring])));
  }, [ready, referenceZone]);

  // ---- The vertices, while somebody is placing them --------------------
  // Only while editing: a saved polygon is a boundary, not a set of handles,
  // and drawing the handles on the guest's copy would invite them to try.
  useEffect(() => {
    const source = vertexSourceRef.current;
    if (!ready || !source) return;

    source.clear();
    if (!onPointsChanged || zone?.kind !== 'Polygon') return;

    source.add(
      zone.points.map(
        (point, index) => new atlas.data.Feature(new atlas.data.Point([point.longitude, point.latitude]), { index }),
      ),
    );
  }, [ready, zone, onPointsChanged]);

  // ---- Where the machine is -------------------------------------------
  useEffect(() => {
    const source = machineSourceRef.current;
    if (!ready || !source) return;

    source.clear();
    if (machineLatitude == null || machineLongitude == null) return;
    source.add(new atlas.data.Feature(new atlas.data.Point([machineLongitude, machineLatitude])));
  }, [ready, machineLatitude, machineLongitude]);

  // ---- Keep the zone in view ------------------------------------------
  // Only follows the CENTRE, never the radius: re-framing on every step of
  // the radius slider would yank the map out from under the thumb dragging it.
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || !zone) return;

    // A polygon has no centre to follow, so it is framed by its own corners
    // instead -- but never while it is being drawn: moving the map between
    // two taps would put the next corner somewhere the host did not aim.
    if (zone.kind === 'Polygon') {
      if (onPointsChanged || zone.points.length < 2) return;
      map.setCamera({
        bounds: atlas.data.BoundingBox.fromPositions(zone.points.map((p) => [p.longitude, p.latitude])),
        padding: 48,
        type: 'ease',
        duration: 400,
      });
      return;
    }

    if (zone.centerLatitude == null || zone.centerLongitude == null) return;
    map.setCamera({
      center: [zone.centerLongitude, zone.centerLatitude],
      zoom: zoomForRadius(zone.radiusMetres ?? 500),
      type: 'ease',
      duration: 400,
    });
    // radiusMetres is deliberately NOT a dependency: re-framing on every
    // step of the slider would yank the map out from under the thumb
    // dragging it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, zone?.kind, zone?.centerLatitude, zone?.centerLongitude, polygonBoundsKey]);

  if (unavailable) {
    return (
      <div
        style={{
          height,
          borderRadius: 14,
          background: token.colorFillQuaternary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          padding: 16,
        }}
      >
        <Text type="secondary" style={{ fontSize: 13 }}>
          {translate(t, TRANSLATIONS.GEOFENCE_MAP_UNAVAILABLE)}
        </Text>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', height, borderRadius: 14, overflow: 'hidden' }}>
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
      {!ready && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: token.colorBgContainer,
          }}
        >
          <Spin />
        </div>
      )}
    </div>
  );
};

/** The zone as a closed ring of [longitude, latitude], whichever shape it is. */
const toRing = (zone: IGeofenceZone): [number, number][] => {
  if (zone.kind === 'Circle') {
    if (zone.centerLatitude == null || zone.centerLongitude == null || !zone.radiusMetres) return [];
    return circleToRing(zone.centerLatitude, zone.centerLongitude, zone.radiusMetres);
  }

  if (zone.points.length < 3) return [];
  const ring = zone.points.map((p) => [p.longitude, p.latitude] as [number, number]);
  // GeoJSON wants the first position repeated last; the backend stores the
  // vertices without that closing duplicate.
  ring.push(ring[0]);
  return ring;
};

/** Where the map opens. A polygon has no centre, so it opens on its first corner. */
const initialCenter = (zone?: IGeofenceZone): [number, number] => {
  if (zone?.centerLatitude != null && zone?.centerLongitude != null) return [zone.centerLongitude, zone.centerLatitude];
  const first = zone?.points?.[0];
  return first ? [first.longitude, first.latitude] : [0, 0];
};

const initialZoom = (zone?: IGeofenceZone): number => {
  if (zone?.centerLatitude != null) return zoomForRadius(zone.radiusMetres ?? 500);
  // A polygon: near enough to see a site, and the bounds effect refines it
  // as soon as there are two corners to frame.
  return zone?.points?.length ? 15 : 5;
};

export default GeofenceMap;
